import { useState, useEffect, useCallback } from 'react';

const API = '/api/intel';

export interface Hotspot {
  lat: number;
  lon: number;
  count: number;
  topCategory?: string;
  topType?: string;
  categories: Record<string, number>;
}

export interface PatternData {
  hourly: number[];   // 24 buckets
  daily: number[];    // 7 buckets (Sun=0)
  monthly: number[];  // 12 buckets
  topTypes: { type: string; count: number; pct: number }[];
  total: number;
}

export interface IntelStats {
  total: number;
  byCategory: Record<string, number>;
  byYear: Record<string, number>;
}

export interface HeatCell {
  lat: number;
  lon: number;
  score: number;
}

const CACHE: Record<string, { data: any; ts: number }> = {};
const TTL = 5 * 60 * 1000; // 5 min

async function cachedFetch(url: string) {
  const hit = CACHE[url];
  if (hit && Date.now() - hit.ts < TTL) return hit.data;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${r.status}`);
  const data = await r.json();
  CACHE[url] = { data, ts: Date.now() };
  return data;
}

export function useIntelStats() {
  const [stats, setStats] = useState<IntelStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cachedFetch(`${API}/stats`)
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return { stats, loading };
}

export function useHotspots(type = 'ALL', bbox?: [number, number, number, number]) {
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const params = new URLSearchParams({ type, limit: '15' });
    if (bbox) params.set('bbox', bbox.join(','));
    setLoading(true);
    cachedFetch(`${API}/hotspots?${params}`)
      .then((d) => setHotspots(d.hotspots || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [type, bbox?.join(',')]);

  useEffect(() => { load(); }, [load]);

  return { hotspots, loading, reload: load };
}

export function usePatterns(lat: number | null, lon: number | null, radius = 0.5) {
  const [patterns, setPatterns] = useState<PatternData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!lat || !lon) return;
    setLoading(true);
    cachedFetch(`${API}/patterns?lat=${lat}&lon=${lon}&radius=${radius}`)
      .then(setPatterns)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [lat, lon, radius]);

  return { patterns, loading };
}

export function useHeatmap(bbox: [number, number, number, number] | null, type = 'ALL') {
  const [cells, setCells] = useState<HeatCell[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback((b: [number, number, number, number]) => {
    const params = new URLSearchParams({ bbox: b.join(','), type, gridSize: '40' });
    setLoading(true);
    cachedFetch(`${API}/heatmap?${params}`)
      .then((d) => setCells(d.cells || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(() => {
    if (bbox) load(bbox);
  }, [bbox?.join(','), type]);

  return { cells, loading };
}
